import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { FaTimes } from 'react-icons/fa';

const ScannerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ScannerContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  max-width: 90vw;
  max-height: 90vh;
  position: relative;
  overflow: auto;
`;

const ScannerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: #333;
  }
`;

const ScannerElement = styled.div`
  width: 100%;
  max-width: 400px;
  
  #reader {
    border: none !important;
  }
  
  #reader__dashboard_section {
    display: none !important;
  }
  
  #reader__camera_selection {
    margin-bottom: 10px;
  }
`;

const Instructions = styled.div`
  text-align: center;
  color: #666;
  margin-bottom: 20px;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 15px;
  border: 1px solid #f5c6cb;
  font-size: 14px;
`;

const WarningBox = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  border: 1px solid #ffeaa7;
  font-size: 14px;
  line-height: 1.6;
  
  strong {
    display: block;
    margin-bottom: 8px;
    font-size: 15px;
  }
  
  ul {
    margin: 10px 0;
    padding-left: 20px;
  }
  
  li {
    margin: 5px 0;
  }
`;

const FileUploadSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  border: 2px dashed #ddd;
  border-radius: 8px;
  text-align: center;
  background-color: #f8f9fa;
`;

const FileInput = styled.input`
  display: none;
`;

const FileLabel = styled.label`
  display: inline-block;
  padding: 12px 24px;
  background-color: #007bff;
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0056b3;
  }
`;

const FileInfo = styled.p`
  margin-top: 10px;
  color: #666;
  font-size: 13px;
`;

const BarcodeScanner = ({ onDetected, onClose }) => {
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);
  const [scanner, setScanner] = useState(null);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [isHttpContext, setIsHttpContext] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cameraTimeout;
    let isComponentMounted = true;

    const initializeScanner = async () => {
      console.log('Initializing the scanner...');
      setError(null);

      // Verifica che l'elemento esista
      const readerElement = document.getElementById("reader");
      if (!readerElement) {
        console.error('Reader element not found');
        setError('Scanner item not found');
        return;
      }

      // Controlla prima se siamo in HTTP
      const isHttp = window.location.protocol === 'http:' && 
                     window.location.hostname !== 'localhost' && 
                     window.location.hostname !== '127.0.0.1';
      
      if (isHttp) {
        console.warn('⚠️ HTTP connection detected (not HTTPS)');
        setIsHttpContext(true);
        setShowFileUpload(true);
        setError(null);
        return;
      }

      // Rileva se siamo su dispositivo mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isAndroid = /Android/i.test(navigator.userAgent);
      console.log(`Mobile device detected: ${isMobile}, Android: ${isAndroid}`);

      // Su Android con HTTPS e certificati self-signed, salta il test della fotocamera
      // and directly show the file upload option
      if (isAndroid && window.location.protocol === 'https:' && window.location.hostname !== 'localhost') {
        console.warn('⚠️ Android detected with HTTPS - likely self-signed certificate');
        setError('On Android, self-signed SSL certificates block camera access. Use file upload.');
        setShowFileUpload(true);
        return;
      }

      // Test accesso fotocamera con vincoli specifici per mobile
      try {
        console.log('Camera Access Test...');
        
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Camera API unavailable. Please use file upload.');
        }

        // Vincoli video ottimizzati per mobile e Android
        const constraints = {
          video: {
            facingMode: { ideal: 'environment' }, // Prefer rear camera
            width: { ideal: isMobile ? 1280 : 1920 },
            height: { ideal: isMobile ? 720 : 1080 }
          }
        };

        console.log('Request camera access with constraints:', constraints);
        
        // Timeout per il test della fotocamera (3 secondi)
        const streamPromise = navigator.mediaDevices.getUserMedia(constraints);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Camera timeout')), 3000)
        );

        const stream = await Promise.race([streamPromise, timeoutPromise]);
        
        // Verify that the stream is active
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack || videoTrack.readyState !== 'live') {
          throw new Error('Camera unavailable or busy');
        }

        console.log('✅ Accessible camera:', videoTrack.label);
        
        // Important: stop the test stream before starting the scanner
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('Track stopped:', track.kind);
        });

        // Wait a bit before launching scanner to avoid conflicts
        await new Promise(resolve => setTimeout(resolve, 300));

      } catch (permissionError) {
        console.error('❌ Camera access error:', permissionError);
        let errorMessage = '';
        
        if (permissionError.message === 'Timeout fotocamera') {
          errorMessage = 'Camera access timeout. On Android with self-signed certificates, use file upload.';
        } else if (permissionError.name === 'NotAllowedError') {
          errorMessage = 'Camera permissions denied. Enable permissions in the browser settings.';
        } else if (permissionError.name === 'NotFoundError') {
          errorMessage = 'No camera found on the device.';
        } else if (permissionError.name === 'NotReadableError' || permissionError.name === 'AbortError') {
          errorMessage = 'Camera already in use or not accessible. Close other apps using the camera.';
        } else if (permissionError.name === 'NotSupportedError' || permissionError.name === 'TypeError') {
          errorMessage = 'Camera not supported by this browser. Use file upload.';
        } else if (permissionError.name === 'SecurityError') {
          errorMessage = 'Invalid SSL certificate. The browser blocks camera access for security. Use file upload.';
        } else {
          errorMessage = `Camera error: ${permissionError.message}. Use file upload.`;
        }
        
        setError(errorMessage);
        setShowFileUpload(true);
        return;
      }

      if (!isComponentMounted) return;

      // Configurazione ottimizzata per mobile
      const config = {
        fps: isMobile ? 5 : 10, // Lower FPS on mobile for performance
        qrbox: isMobile ? { width: 250, height: 250 } : 250,
        aspectRatio: isMobile ? 1.0 : 1.777778,
        disableFlip: false,
        rememberLastUsedCamera: true,
        supportedScanTypes: [0, 1, 2, 3, 4, 5, 6, 7, 8], // All formats
        showTorchButtonIfSupported: true,
        showZoomSliderIfSupported: true,
        defaultZoomValueIfSupported: 2,
      };

      const html5QrcodeScanner = new Html5QrcodeScanner(
        "reader",
        config,
        /* verbose= */ false // Disable verbose logging
      );

      const onScanSuccess = (decodedText, decodedResult) => {
        console.log(`✅ Code found: ${decodedText}`);
        if (isComponentMounted) {
          onDetected(decodedText);
          html5QrcodeScanner.clear().catch(console.error);
        }
      };

      const onScanFailure = (error) => {
        // Ignore normal scan failures (not found)
      };

      try {
        console.log('🎥 Starting HTML5-QRCode scanner...');
        await html5QrcodeScanner.render(onScanSuccess, onScanFailure);
        
        if (!isComponentMounted) {
          html5QrcodeScanner.clear().catch(console.error);
          return;
        }

        setScanner(html5QrcodeScanner);
        setError(null);
        console.log('✅ Scanner started successfully');

        // Reduced timeout: after 4s if camera not stable, show fallback
        cameraTimeout = setTimeout(() => {
          if (isComponentMounted) {
            console.warn('⏱️ Camera timeout - activating fallback');
            setError('The camera is taking too long or is blocked by the SSL certificate. Use file upload.');
            setShowFileUpload(true);
          }
        }, 4000);

      } catch (err) {
        console.error('❌ Scanner start error:', err);
        if (isComponentMounted) {
          setError(`Unable to start scanner: ${err.message}. Use file upload.`);
          setShowFileUpload(true);
        }
      }
    };

    initializeScanner();

    return () => {
      isComponentMounted = false;
      if (cameraTimeout) {
        clearTimeout(cameraTimeout);
      }
      if (scanner) {
        console.log('🧹 Cleaning up scanner...');
        scanner.clear().catch(console.error);
      }
    };
  }, [onDetected, scanner]);

  const handleClose = () => {
    if (scanner) {
      scanner.clear().catch(console.error);
    }
    onClose();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const html5QrCode = new Html5Qrcode("reader");
      
      const result = await html5QrCode.scanFile(file, true);
      console.log('Code detected from file:', result);
      onDetected(result);
      handleClose();
    } catch (err) {
      console.error('Error scanning file:', err);
      setError(`Unable to read the code from the image: ${err}`);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <ScannerOverlay onClick={handleOverlayClick}>
      <ScannerContainer>
        <ScannerHeader>
          <Title>Scan Barcode</Title>
          <CloseButton onClick={handleClose}>
            <FaTimes />
          </CloseButton>
        </ScannerHeader>

        {isHttpContext && (
          <WarningBox>
            <strong>⚠️ Camera Not Available (HTTP)</strong>
            <p>
              Modern browsers require a secure connection (HTTPS) to access the camera 
              for security reasons.
            </p>
            <p><strong>Solutions:</strong></p>
            <ul>
              <li><strong>Option 1:</strong> Upload a photo of the barcode using the button below</li>
              <li><strong>Option 2:</strong> Configure HTTPS on the server to use the real-time camera</li>
              <li><strong>Option 3:</strong> Manually type the code into the search box</li>
            </ul>
          </WarningBox>
        )}

        {!isHttpContext && (
          <Instructions>
            Position the barcode inside the frame to scan it
          </Instructions>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {!showFileUpload && (
          <ScannerElement ref={scannerRef}>
            <div id="reader"></div>
          </ScannerElement>
        )}

        {showFileUpload && (
          <FileUploadSection>
            <h3 style={{ marginTop: 0, color: '#333' }}>📷 Upload Barcode Photo</h3>
            <p style={{ color: '#666', marginBottom: '15px' }}>
              Take a photo of the barcode or choose an image from the gallery
            </p>
            <FileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileUpload}
              id="barcode-file-input"
            />
            <FileLabel htmlFor="barcode-file-input">
              📸 Choose or Take Photo
            </FileLabel>
            <FileInfo>
              Supports: JPG, PNG, BMP • The code will be detected automatically
            </FileInfo>
          </FileUploadSection>
        )}
      </ScannerContainer>
    </ScannerOverlay>
  );
};

export default BarcodeScanner;